import React, { useState, useEffect, useContext } from 'react';
import {
  Button,
  ButtonGroup,
  Banner,
  TextStyle,
  DisplayText
} from '@superops/ui-design-system';
import {
  AccountClient,
  GET_ESTIMATE_FOR_ALL_LINEITEMS,
  GET_ESTIMATE_FOR_LINEITEM_UPGRADE
} from '@nile/graphql';
import { manageList } from '@nile/banyan/src/Utils/pricingHelper';
import {
  AppContext,
  Loader,
  getTranslatedText,
  useActivityTracker
} from '@nile/components';
import { TITLE_MAP } from '@nile/components/src/Constants/pricing';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { PricingSummaryCardWrapper } from '@nile/components/src/Cards/PricingSummaryCard';
import { PlanItemContainer } from '@nile/components/src/Cards';
import { isEmpty, isEqual, sortBy } from 'lodash';

const UpgradeLicenseSheet = (props) => {
  const {
    subscription,
    plan,
    tenure,
    currency,
    locale,
    onUpgradeHandler,
    handleManageModal,
    superTip,
    isSamePlan,
    isAddEndTech
  } = props;

  const intl = useIntl();

  const app = useContext(AppContext);

  const { user } = app;

  const onSoloPlanHandler = () => {
    const userMeta = {
      email: user.email,
      name: user.name,
      userId: user.userId,
      subscriptionStatus: user.subscriptionStatus,
      currentPlan: subscription?.currentPlanPrice?.plan?.planName,
      billingPeriod: subscription?.billingPeriod,
      licenseCount: subscription?.licenseCount,
      addOnsCount: subscription?.subscribedAddons?.length,
      nextBillingDateTime: dayjs(subscription?.nextBillingDateTime).format(
        'MMM DD, YYYY'
      )
    };
    window.Intercom('trackEvent', 'solo-plan', userMeta);
  };

  const [changeTimeout, setChangeTimeout] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [intitalEstimateLoading, setIntitalEstimateLoading] = useState(false);

  const [planEstimateState, setPlanEstimate] = useState({});
  const [licenseCount, setLicenseCount] = useState({});
  const [actualLicenseCount, setActualLicenseCount] = useState({});
  const [minimumLicenseCount, setMinimumLicenseCount] = useState({});
  const [invoiceState, setInvoiceState] = useState({});
  const [offeringEntity, setOfferingEntity] = useState([]);

  const getEstimatedPriceForLineItem = (data = null, quantity = null) => {
    setIsEstimating(true);
    const estimateData = data || planEstimateState;
    const quantityData = quantity || licenseCount;
    AccountClient.query({
      query: GET_ESTIMATE_FOR_ALL_LINEITEMS,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          plan: {
            planPrice: { id: estimateData?.plan?.planPrice?.id },
            quantity: quantityData.TECHNICIAN
          },
          addons: estimateData?.addons?.map((el) => ({
            addonPrice: { id: el?.addonPrice?.id },
            quantity: quantityData[el?.addonPrice?.chargeBeeAddonPriceId] || 0
          }))
        }
      }
    })
      .then((res) => {
        const quantity = {};
        if (res?.data?.getProductSubscriptionEstimateForLineItems) {
          const result = res.data.getProductSubscriptionEstimateForLineItems;
          const termResult = result?.termInvoiceEstimate;
          setPlanEstimate(termResult);
          setOfferingEntity(result?.offeringEntities);
          setInvoiceState(result?.immediateInvoiceEstimate);
          quantity.TECHNICIAN = termResult?.plan?.quantity;
          if (!isEmpty(termResult?.addons)) {
            sortBy(termResult?.addons, (obj) => obj?.addonPrice?.id)?.map(
              (el) => {
                quantity[el?.addonPrice?.chargeBeeAddonPriceId] = el?.quantity;
              }
            );
          }

          setLicenseCount((prevState) => ({ ...prevState, ...quantity }));
        }
      })
      .finally(() => setIsEstimating(false));
  };

  const getEstimateForExistingLineItem = (tenureChange = false) => {
    if (tenureChange) {
      setIsEstimating(true);
    } else {
      setIntitalEstimateLoading(true);
    }
    const planPriceId = plan?.planPrices?.find(
      (prPlan) =>
        prPlan?.currency?.currencyCode === currency &&
        prPlan?.duration === tenure &&
        prPlan?.durationValue === 1
    );

    const planIP =
      isAddEndTech && !tenureChange ? {} : { input: { id: planPriceId?.id } };

    AccountClient.query({
      query: GET_ESTIMATE_FOR_LINEITEM_UPGRADE,
      fetchPolicy: 'network-only',
      variables: planIP
    })
      .then((res) => {
        const quantity = {};
        const minimumQuantity = {};
        if (res?.data?.getProductSubscriptionEstimateForExistingLineItems) {
          const result =
            res.data.getProductSubscriptionEstimateForExistingLineItems;
          const termResult = result?.termInvoiceEstimate;
          setPlanEstimate(termResult);
          setOfferingEntity(result?.offeringEntities);
          setInvoiceState(result?.immediateInvoiceEstimate);
          quantity.TECHNICIAN = termResult?.plan?.quantity;
          minimumQuantity.TECHNICIAN = termResult?.plan?.minimumQuantity;
          if (!isEmpty(termResult?.addons)) {
            sortBy(termResult?.addons, (obj) => obj?.addonPrice?.id)?.map(
              (el) => {
                quantity[el?.addonPrice?.chargeBeeAddonPriceId] = el?.quantity;
                minimumQuantity[el?.addonPrice?.chargeBeeAddonPriceId] =
                  el?.minimumQuantity < 0 ? 0 : el?.minimumQuantity;
              }
            );
          }
          setActualLicenseCount((prevState) => ({
            ...prevState,
            ...quantity
          }));
          setLicenseCount((prevState) => ({ ...prevState, ...quantity }));
          setMinimumLicenseCount((prevState) => ({
            ...prevState,
            ...minimumQuantity
          }));
          if (tenureChange) {
            getEstimatedPriceForLineItem(termResult, quantity);
          }
        }
      })
      .finally(() => {
        if (!tenureChange) {
          setIntitalEstimateLoading(false);
        }
      });
  };

  const onValueChange = () => {
    if (!isEmpty(planEstimateState)) {
      setChangeTimeout(
        setTimeout(function() {
          getEstimatedPriceForLineItem();
        }, 1000)
      );
      if (changeTimeout) {
        clearTimeout(changeTimeout);
      }
    }
  };

  const decrementHandler = (type) => {
    setLicenseCount((prevState) => ({
      ...prevState,
      [type]: prevState[type] - 1
    }));
  };

  const incrementHandler = (type) => {
    setLicenseCount((prevState) => ({
      ...prevState,
      [type]: prevState[type] + 1
    }));
  };

  const upgradeLicense = () => {
    useActivityTracker({
      event: 'pricing_upgrade_payment_btn'
    });
    onUpgradeHandler(planEstimateState, licenseCount);
  };

  useEffect(() => {
    onValueChange();
  }, [JSON.stringify(licenseCount)]);

  useEffect(() => {
    if (!isEmpty(planEstimateState) && !intitalEstimateLoading) {
      setChangeTimeout(
        setTimeout(function() {
          getEstimateForExistingLineItem(true);
        }, 200)
      );
      if (changeTimeout) {
        clearTimeout(changeTimeout);
      }
    }
  }, [tenure]);

  useEffect(() => {
    getEstimateForExistingLineItem();
  }, [plan]);

  if (intitalEstimateLoading) {
    return (
      <div className="fx fh fw min-height-300">
        <Loader />
      </div>
    );
  }

  return (
    <PricingSummaryCardWrapper>
      <PlanItemContainer
        selectedPlan={plan}
        type="TECHNICIAN"
        showManageButton
        decrementHandler={decrementHandler}
        incrementHandler={incrementHandler}
        planEstimate={planEstimateState}
        includeAddon={!isEmpty(planEstimateState?.addons)}
        licenseCount={licenseCount}
        actualLicenseCount={actualLicenseCount}
        minimumLicenseCount={minimumLicenseCount}
        handleManageModal={handleManageModal}
        tenure={tenure}
        currency={currency}
        locale={locale}
      />
      {planEstimateState?.addons?.map((addon) =>
        addon?.addon?.offeringEntityType !== 'MDM_ASSET' && (
          <PlanItemContainer
            selectedPlan={plan}
            type={addon?.addonPrice?.chargeBeeAddonPriceId}
            decrementHandler={decrementHandler}
            incrementHandler={incrementHandler}
            tenure={tenure}
            currency={currency}
            locale={locale}
            showManageButton={manageList.includes(
              addon?.addon?.offeringEntityType
            )}
            handleManageModal={handleManageModal}
            addon={addon}
            includeAddon
            isAddon
            licenseCount={licenseCount}
            actualLicenseCount={actualLicenseCount}
            minimumLicenseCount={minimumLicenseCount}
            monitorCount={offeringEntity.find(
              (el) => el?.offeringEntityType === addon?.addon?.offeringEntityType
            )}
          />
        )
      )}

      <>
        <div>
          <>
            {isEstimating ? (
              <div className="mt-25 bill-total-holder py20">
                <div className="fx actr h-30">
                  <Loader />
                </div>
              </div>
            ) : (
              <>
                {!isEmpty(invoiceState) ? (
                  <>
                    {isAddEndTech ? (
                      <>
                        <div className="mt-25 bill-total-holder py20">
                          {licenseCount?.TECHNICIAN >
                          actualLicenseCount?.TECHNICIAN ? (
                            <div className="fx">
                              <div className="left-billing-section">
                                <TextStyle>
                                  {`${getTranslatedText(
                                    intl,
                                    TITLE_MAP.TECHNICIAN
                                  )} ${invoiceState?.plan?.quantity} x
                                ${locale &&
                                  currency &&
                                  tenure &&
                                  new Intl.NumberFormat(locale, {
                                    style: 'currency',
                                    currency,
                                    minimumFractionDigits: 0,
                                    currencyDisplay: 'narrowSymbol'
                                  }).format(invoiceState?.plan?.unitPrice)}`}
                                </TextStyle>
                              </div>
                              <div className="right-billing-section">
                                <TextStyle weight="Bold">
                                  {locale &&
                                    currency &&
                                    tenure &&
                                    new Intl.NumberFormat(locale, {
                                      style: 'currency',
                                      currency,
                                      minimumFractionDigits: 2,
                                      currencyDisplay: 'narrowSymbol'
                                    }).format(invoiceState?.plan?.amount)}
                                </TextStyle>
                              </div>
                            </div>
                          ) : (
                            <></>
                          )}
                          {invoiceState?.addons?.map((addon) => (
                            <>
                              {licenseCount?.[
                                addon?.addonPrice?.chargeBeeAddonPriceId
                              ] >
                              actualLicenseCount?.[
                                addon?.addonPrice?.chargeBeeAddonPriceId
                              ] ? (
                                <div
                                  className={`fx ${
                                    licenseCount?.TECHNICIAN >
                                    actualLicenseCount?.TECHNICIAN
                                      ? 'mt10'
                                      : ''
                                  }`}
                                >
                                  <div className="left-billing-section">
                                    <TextStyle>
                                      {`${getTranslatedText(
                                        intl,
                                        TITLE_MAP[
                                          addon?.addon?.offeringEntityType
                                        ]
                                      )}  ${addon?.quantity} x
                                    ${locale &&
                                      currency &&
                                      tenure &&
                                      new Intl.NumberFormat(locale, {
                                        style: 'currency',
                                        currency,
                                        minimumFractionDigits: 0,
                                        currencyDisplay: 'narrowSymbol'
                                      }).format(addon?.unitPrice)}`}
                                    </TextStyle>
                                  </div>
                                  <div className="right-billing-section">
                                    <TextStyle weight="Bold">
                                      {locale &&
                                        currency &&
                                        tenure &&
                                        new Intl.NumberFormat(locale, {
                                          style: 'currency',
                                          currency,
                                          minimumFractionDigits: 2,
                                          currencyDisplay: 'narrowSymbol'
                                        }).format(addon?.amount)}
                                    </TextStyle>
                                  </div>
                                </div>
                              ) : (
                                <></>
                              )}
                            </>
                          ))}
                          <div
                            className={` ${
                              !isEqual(actualLicenseCount, licenseCount)
                                ? 'mt10'
                                : ''
                            } billing-wrapper`}
                          >
                            <InvoiceSplit
                              currency={currency}
                              locale={locale}
                              invoice={invoiceState}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="mt-25 bill-total-holder py20">
                        <div
                          className={` ${
                            !isEqual(actualLicenseCount, licenseCount)
                              ? 'mt10'
                              : ''
                          } billing-wrapper`}
                        >
                          <InvoiceSplit
                            currency={currency}
                            locale={locale}
                            invoice={invoiceState}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </>
            )}
          </>
        </div>
      </>
      <div className="mt-20">
        <Banner status="info">
          <p className="desc italic">{superTip}</p>
        </Banner>
      </div>
      <div className="mt-20 flex jend">
        <ButtonGroup>
          <Button
            plain={plan?.chargeBeePlanId !== 'solo'}
            primary={plan?.chargeBeePlanId === 'solo'}
            onClick={
              plan?.chargeBeePlanId === 'solo'
                ? () => onSoloPlanHandler()
                : () => window.Intercom('show')
            }
          >
            <FormattedMessage id="admin.billing.upgradelicense.contactsupport" />
          </Button>

          {plan?.chargeBeePlanId !== 'solo' && (
            <Button
              primary
              onClick={upgradeLicense}
              disabled={isSamePlan && isEmpty(invoiceState)}
            >
              <FormattedMessage id="admin.billing.upgradelicense.payment" />
            </Button>
          )}
        </ButtonGroup>
      </div>
    </PricingSummaryCardWrapper>
  );
};

const InvoiceSplit = ({ locale, currency, invoice }) => {
  return (
    <>
      <div className="fx mb5">
        <div className="left-billing-section">
          <TextStyle>Total</TextStyle>
        </div>
        <div className="right-billing-section">
          <TextStyle weight="Bold">
            {locale &&
              currency &&
              new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                currencyDisplay: 'narrowSymbol'
              }).format(invoice?.total)}
          </TextStyle>
        </div>
      </div>
      <div className="fx mb5">
        <div className="left-billing-section">
          <TextStyle>Credits Applied</TextStyle>
        </div>
        <div className="right-billing-section">
          <TextStyle weight="Bold">
            {locale &&
              currency &&
              new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                currencyDisplay: 'narrowSymbol'
              }).format(invoice?.creditsApplied)}
          </TextStyle>
        </div>
      </div>
      <div className="fx greenText">
        <div className="left-billing-section">
          <DisplayText size="extraLarge">Pay now</DisplayText>
        </div>
        <div className="right-billing-section">
          <DisplayText size="extraLarge" weight="Bold">
            {locale &&
              currency &&
              new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                currencyDisplay: 'narrowSymbol'
              }).format(invoice?.amountDue)}
          </DisplayText>
        </div>
      </div>
    </>
  );
};

export default UpgradeLicenseSheet;
